import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { errorMsg, getFullName } from '../../../../Apis/functions';
import { permissionObj } from '../../../../Helpers/permission';
import { USER_TYPE_NAME_MODEL } from '../../../../enums/userTypeEnum';
import {
    CreateEventMonitoringLayout,
    CreateEventMonitoringPanel,
    DeleteEventMonitoringLayout,
    DeleteEventMonitoringPanel,
    GetEventMonitoringActiveLayout,
    GetEventMonitoringPanelAreaEvents,
    GetEventMonitoringPanelAreas,
    GetEventMonitoringPanelEvents,
    GetEventMonitoringReports,
    SetActiveEventMonitoringLayout,
    SetEventMonitoringWalkthroughDone,
    UpdateEventMonitoringLayout,
    UpdateEventMonitoringPanel,
    UpdateEventMonitoringPanelsGeometry
} from '../../../../reduxToolkit/EventMonitoring/EventMonitoringApi';
import { GetUserPreferencesByUserId } from '../../../../reduxToolkit/UserPreferences/UserPreferencesApi';
import { useEventMonitoringContextProvider } from './EventMonitoringContextProvider';
import { EventMonitoringWebSocket } from './EventMonitoringWebSocket';
import {
    addEventsToAreaFeeds,
    addEventsToAreas,
    getOldestCursor,
    getPanelEvents,
    getPanelSourceKey,
    getPanelView,
    isConfiguredPanel,
    isReportsPanel,
    isZoneOverviewPanel,
    mergeEvents,
    replaceFirstPage
} from './EventMonitoringCommon';
import {
    AREA_FEED_MODEL,
    AREA_MODEL,
    CREATE_LAYOUT_MODEL,
    DELETE_TARGET,
    EVENT_MODEL,
    GRID_UNITS,
    getCellUnits,
    getLayoutType,
    getPanelShape,
    getPanelsGeometry,
    getStartOfDay,
    getWalkthroughMockLayout,
    getWalkthroughSteps,
    LAYOUT_MODEL,
    LAYOUTS_MODAL_TAB,
    PANEL_FEED_MODEL,
    PANEL_MODEL,
    PANEL_PAGE_SIZE,
    PANEL_VIEW,
    WALKTHROUGH_MODAL,
    WALKTHROUGH_PERMISSION
} from '../Model/Model';

/**
 * @Author      Karen Yunuen Pozos Arias
 * @JobTitle    Full Stack
 * @Email       kpozos@ibl.mx
 *
 * State of the Event Monitoring screen: connection, events, layouts, panels and modals.
 * The layout of the canvas is read from the backend when the screen opens and every change is stored there;
 * the context keeps it while the admin navigates inside the module. The saved layouts are searched by their tab.
 */
const EventMonitoringMainApp = () => {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const { paramsContext, setParamsContext } = useEventMonitoringContextProvider();
    const [isOnline, setIsOnline] = useState(false);
    const [modified, setModified] = useState(false);
    // Events read for every panel, by its id //
    const [panelFeeds, setPanelFeeds] = useState({});
    const reports = useSelector((state) => state.ReportSlice.notificationList?.reports);
    const user = useSelector((state) => state.authenticatioauthennSlice.user?.data);
    const DEV = useSelector((state) => state?.CommonsSlice?.development);
    const { permission } = useSelector((state) => state.authenticatioauthennSlice);
    const preferences = useSelector((state) => state.UserPreferencesSlice?.getUserPreferencesByUserId);
    // The walkthrough is shown once per visit of the module, even before the preferences are read again //
    const walkthroughShownRef = useRef(false);
    // Create layouts and add panels / rename layouts, configure and resize panels / delete layouts and panels //
    // The guard carries no permissions and does every action of the module //
    const isGuard = user?.userType?.name === USER_TYPE_NAME_MODEL.GUARD;
    const canRead = DEV || isGuard || permission?.includes(permissionObj?.WEB_EVENT_MONITORING_READ);
    const canCreate = DEV || isGuard || permission?.includes(permissionObj?.WEB_EVENT_MONITORING_CREATE);
    const canUpdate = DEV || isGuard || permission?.includes(permissionObj?.WEB_EVENT_MONITORING_UPDATE);
    const canDelete = DEV || isGuard || permission?.includes(permissionObj?.WEB_EVENT_MONITORING_DELETE);
    // The walkthrough only explains what the permissions of the admin let it do //
    const walkthroughSteps = getWalkthroughSteps({
        [WALKTHROUGH_PERMISSION.READ]: canRead,
        [WALKTHROUGH_PERMISSION.CREATE]: canCreate,
        [WALKTHROUGH_PERMISSION.UPDATE]: canUpdate,
        [WALKTHROUGH_PERMISSION.DELETE]: canDelete,
    });
    // Latest user, read by the pages of reports requested from an older render //
    const userRef = useRef(user);
    userRef.current = user;
    const [now, setNow] = useState(Date.now());
    // Day the counters of the zone overviews belong to //
    const dayRef = useRef(getStartOfDay(Date.now()));
    const [showLayoutsModal, setShowLayoutsModal] = useState(false);
    const [layoutsTab, setLayoutsTab] = useState(LAYOUTS_MODAL_TAB.CREATE.ID);
    // Reloads the page of the Saved Layouts tab after a layout is renamed or deleted //
    const [savedLayoutsModified, setSavedLayoutsModified] = useState(false);
    const [showAddPanelModal, setShowAddPanelModal] = useState(false);
    const [panelToConfigure, setPanelToConfigure] = useState(null);
    const [panelToExpand, setPanelToExpand] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    // Title and element of the delete modal for the panel or layout being deleted //
    const deleteTargetModel = deleteTarget?.type === DELETE_TARGET.PANEL.ID ? DELETE_TARGET.PANEL : DELETE_TARGET.LAYOUT;
    const [walkthroughStep, setWalkthroughStep] = useState(null);
    // Latest context, read by the handlers the pointer listeners of the resize keep from an older render //
    const paramsRef = useRef(paramsContext);
    paramsRef.current = paramsContext;
    // Latest feeds, read by the socket handlers registered on the first render //
    const feedsRef = useRef(panelFeeds);
    feedsRef.current = panelFeeds;
    // Pages being read, by panel and source, so a page is never requested twice //
    const loadingRef = useRef(new Set());

    const activeLayout = paramsContext?.layout ?? null;

    /**
     * Places a layout read from the backend in the canvas. The events of the previous one are dropped,
     * the panels of the new layout read their first page.
     */
    const showLayout = (layout) => {
        setPanelFeeds({});
        setParamsContext((prev) => ({ ...prev, layout: layout ? LAYOUT_MODEL.fromApi(layout) : null }));
    };

    /**
     * Reads the layout loaded in the canvas of the admin.
     */
    const loadActiveLayout = () => {
        dispatch(GetEventMonitoringActiveLayout()).then(({ payload: { data } }) => {
            if (!data?.success) return errorMsg(data?.code);
            showLayout(data.data);
        });
    };

    /**
     * Reads the layout of the canvas.
     */
    useEffect(() => {
        loadActiveLayout();
    }, []);

    /**
     * Report of the notifications or of a page of reports, shaped as an event. The pages carry the reports of every
     * user with the one who requested each; the notifications only carry the ones of the logged user.
     */
    const toReportEvent = (report) => EVENT_MODEL.fromReport(report, report.requestedById
        ? {
            name: getFullName(report.requestedByName, report.requestedByLastName, report.requestedBySecondLastName),
            userTypeId: report.requestedByUserTypeId,
        }
        : {
            name: getFullName(userRef.current?.name, userRef.current?.lastName, userRef.current?.secondLastName),
            userTypeId: userRef.current?.userType?.id,
        });

    /**
     * Start of the day the zone overview shows its alerts since; the rest of the panels show every event.
     */
    const getOverviewSince = (panel) => (isZoneOverviewPanel(panel) ? dayRef.current : null);

    /**
     * Reads the areas of a grouped panel (the zones of the admin in the zone overview) with their counters. They are read
     * with its first page, so the counters follow the events shown. The zone overview counts the events of today, so its
     * areas are read again once the day changes.
     */
    const loadPanelAreas = (panel, sourceKey) => {
        dispatch(GetEventMonitoringPanelAreas({ panelId: panel.id, since: dayRef.current })).then(({ payload: { data } }) => {
            if (!data?.success) return errorMsg(data?.code);

            const areas = (data.data ?? []).map(AREA_MODEL.fromApi);
            setPanelFeeds((prev) => (prev[panel.id]?.sourceKey === sourceKey
                ? { ...prev, [panel.id]: { ...prev[panel.id], areas } }
                : prev));
        });
    };

    /**
     * Reads the next page of events of a panel, the ones older than the oldest one it shows. With reset it reads
     * its first page again over the events it shows (replaceFirstPage); they stay on screen until the page arrives.
     * The report panels read the reports of the admin. A response of a source the panel no longer has is ignored.
     */
    const loadPanelEvents = (panel, reset = false) => {
        if (!isConfiguredPanel(panel)) return;

        const sourceKey = getPanelSourceKey(panel);
        const requestKey = `${panel.id}_${sourceKey}`;
        if (loadingRef.current.has(requestKey)) return;

        const feed = feedsRef.current[panel.id];
        const current = feed?.sourceKey === sourceKey ? feed : null;
        if (!reset && current?.loaded && !current.hasMore) return;

        const before = !reset ? getOldestCursor(current?.events) : null;
        const isReports = isReportsPanel(panel);
        loadingRef.current.add(requestKey);
        setPanelFeeds((prev) => ({ ...prev, [panel.id]: { ...(current ?? PANEL_FEED_MODEL), sourceKey, loading: true } }));
        if (before === null && getPanelView(panel) === PANEL_VIEW.GROUPED) loadPanelAreas(panel, sourceKey);

        const request = isReports
            ? GetEventMonitoringReports({ size: PANEL_PAGE_SIZE, before })
            // The zone overview only shows the alerts of today //
            : GetEventMonitoringPanelEvents({ panelId: panel.id, size: PANEL_PAGE_SIZE, before, since: getOverviewSince(panel) });

        dispatch(request).then(({ payload: { data } }) => {
            loadingRef.current.delete(requestKey);
            if (!data?.success) errorMsg(data?.code);

            const incoming = data?.success ? (data.data ?? []).map(isReports ? toReportEvent : EVENT_MODEL.fromApi) : [];
            setPanelFeeds((prev) => {
                const prevFeed = prev[panel.id];
                if (prevFeed?.sourceKey !== sourceKey) return prev;
                // A failed page keeps the events shown and stops reading, so it is not requested again and again //
                if (!data?.success) return { ...prev, [panel.id]: { ...prevFeed, hasMore: false, loading: false, loaded: true } };

                const events = reset ? replaceFirstPage(prevFeed.events, incoming, PANEL_PAGE_SIZE) : mergeEvents(prevFeed.events, incoming);
                // The older events kept by replaceFirstPage keep their own next page //
                const keptOlder = reset && events.length > incoming.length;
                return {
                    ...prev,
                    [panel.id]: {
                        ...prevFeed,
                        events,
                        hasMore: keptOlder ? prevFeed.hasMore : incoming.length === PANEL_PAGE_SIZE,
                        loading: false,
                        loaded: true,
                    },
                };
            });
        });
    };

    /**
     * Reads the next page of events of an area of a zone panel, the ones older than the oldest one it shows.
     * With reset it reads its first page again over the events it shows, as loadPanelEvents does.
     * A response of a source the panel no longer has is ignored.
     */
    const loadAreaEvents = (panel, areaId, reset = false) => {
        const sourceKey = getPanelSourceKey(panel);
        const requestKey = `${panel.id}_${sourceKey}_${areaId}`;
        const feed = feedsRef.current[panel.id];
        if (feed?.sourceKey !== sourceKey || loadingRef.current.has(requestKey)) return;

        const current = feed.areaFeeds?.[areaId] ?? AREA_FEED_MODEL;
        if (!reset && current.loaded && !current.hasMore) return;

        const before = !reset ? getOldestCursor(current.events) : null;
        const updateAreaFeed = (update) => setPanelFeeds((prev) => {
            const prevFeed = prev[panel.id];
            if (prevFeed?.sourceKey !== sourceKey) return prev;
            const areaFeed = prevFeed.areaFeeds?.[areaId] ?? AREA_FEED_MODEL;
            return { ...prev, [panel.id]: { ...prevFeed, areaFeeds: { ...prevFeed.areaFeeds, [areaId]: update(areaFeed) } } };
        });

        loadingRef.current.add(requestKey);
        updateAreaFeed((areaFeed) => ({ ...areaFeed, loading: true }));

        dispatch(GetEventMonitoringPanelAreaEvents({ panelId: panel.id, areaId, size: PANEL_PAGE_SIZE, before, since: getOverviewSince(panel) })).then(({ payload: { data } }) => {
            loadingRef.current.delete(requestKey);
            if (!data?.success) {
                errorMsg(data?.code);
                // A failed page keeps the events shown and stops reading, so it is not requested again and again //
                return updateAreaFeed((areaFeed) => ({ ...areaFeed, hasMore: false, loading: false, loaded: true }));
            }

            const incoming = (data.data ?? []).map(EVENT_MODEL.fromApi);
            updateAreaFeed((areaFeed) => {
                const events = reset ? replaceFirstPage(areaFeed.events, incoming, PANEL_PAGE_SIZE) : mergeEvents(areaFeed.events, incoming);
                const keptOlder = reset && events.length > incoming.length;
                return {
                    events,
                    hasMore: keptOlder ? areaFeed.hasMore : incoming.length === PANEL_PAGE_SIZE,
                    loading: false,
                    loaded: true,
                };
            });
        });
    };

    /**
     * Places the events of the topic, or the reports of the notifications, in every panel of the canvas
     * they belong to. An event already shown is replaced, so a report shows its new status. The new events
     * are added to the counters of the areas of the zone panels and to the areas already read.
     * Nothing is dropped, so a panel keeps every event it received and can be scrolled to its end.
     */
    const pushPanelEvents = (incoming = []) => {
        const panels = paramsRef.current?.layout?.panels ?? [];
        setPanelFeeds((prev) => panels.reduce((next, panel) => {
            const feed = prev[panel.id];
            // The zone overview only shows the alerts of today, an older one received late is left out //
            const since = getOverviewSince(panel);
            const matched = getPanelEvents(panel, incoming, feed?.areas).filter((event) => since === null || event.date >= since);
            if (feed?.sourceKey !== getPanelSourceKey(panel) || matched.length === 0) return next;

            const shownIds = new Set(feed.events.map((event) => event.id));
            // Only the zone overview has groups (areas), the zones of the admin //
            const areas = feed.areas && addEventsToAreas(feed.areas, matched.filter((event) => !shownIds.has(event.id)));
            const areaFeeds = feed.areas ? addEventsToAreaFeeds(feed.areaFeeds, matched, feed.areas) : feed.areaFeeds;
            return { ...next, [panel.id]: { ...feed, events: mergeEvents(feed.events, matched), areas, areaFeeds } };
        }, prev));
    };

    /**
     * Reads again the first page of every panel and of every area already read, once the socket connects.
     */
    const reloadPanelEvents = () => {
        (paramsRef.current?.layout?.panels ?? [])
            .filter((panel) => feedsRef.current[panel.id])
            .forEach((panel) => {
                loadPanelEvents(panel, true);
                Object.keys(feedsRef.current[panel.id].areaFeeds ?? {}).forEach((areaId) => loadAreaEvents(panel, areaId, true));
            });
    };

    /**
     * Initializes the websocket connection and exposes the manual reconnection handler.
     */
    const { reconnectWebSocket } = EventMonitoringWebSocket({
        isOnline,
        setIsOnline,
        onEvents: pushPanelEvents,
        onReports: (incoming) => pushPanelEvents(incoming.map(toReportEvent)),
        onConnected: reloadPanelEvents,
    });

    /**
     * Reads the first page of the panels of the active layout that were not read yet, or whose source changed.
     */
    useEffect(() => {
        (activeLayout?.panels ?? [])
            .filter((panel) => feedsRef.current[panel.id]?.sourceKey !== getPanelSourceKey(panel))
            .forEach((panel) => loadPanelEvents(panel));
    }, [activeLayout]);

    /**
     * Refreshes the elapsed time of the cards every minute.
     */
    useEffect(() => {
        const interval = setInterval(() => setNow(Date.now()), 60000);
        return () => clearInterval(interval);
    }, []);

    /**
     * Once the day changes the zone overviews drop the alerts of the previous day and read their zones and their first
     * page again, so their counters and their lists start from the new day. The groups expanded read their alerts again.
     */
    useEffect(() => {
        const startOfDay = getStartOfDay(now);
        if (startOfDay === dayRef.current) return;
        dayRef.current = startOfDay;

        const overviews = (paramsRef.current?.layout?.panels ?? [])
            .filter((panel) => isZoneOverviewPanel(panel) && feedsRef.current[panel.id]?.sourceKey === getPanelSourceKey(panel));
        if (overviews.length === 0) return;

        const isOverview = (panelId) => overviews.some((panel) => panel.id === panelId);
        const withoutOverviews = (feeds) => Object.fromEntries(Object.entries(feeds).filter(([panelId]) => !isOverview(panelId)));
        // The ref is cleared too, so the first pages are read from the new day without the events of the previous one //
        feedsRef.current = withoutOverviews(feedsRef.current);
        setPanelFeeds(withoutOverviews);
        overviews.forEach((panel) => loadPanelEvents(panel));
    }, [now]);

    /**
     * Shows the walkthrough the first time the admin opens the module, the preferences of the user say whether it was seen.
     * It only has the steps of the permissions of the admin, so an admin without any of them does not see it.
     */
    useEffect(() => {
        if (walkthroughShownRef.current || !walkthroughSteps.length) return;
        // The preferences are an empty list until they are read and null for a user without them, who has not seen it //
        if (Array.isArray(preferences) || preferences?.eventMonitoringWalkthroughDone) return;
        walkthroughShownRef.current = true;
        setWalkthroughStep(0);
        // The permissions can arrive after the preferences //
    }, [preferences, canRead, canCreate, canUpdate, canDelete]);

    /**
     * Resets the modified flag when the connection is online.
     */
    useEffect(() => {
        if (isOnline) setModified(false);
    }, [isOnline]);

    /**
     * Attempts to reconnect when the renew icon is pressed.
     */
    useEffect(() => {
        if (modified) {
            reconnectWebSocket();
            setModified(false);
        }
    }, [modified]);

    /**
     * Applies a change to the panels of the layout of the canvas. It reads the latest context, so the pointer
     * listeners of the resize and the responses of the backend can call it from an older render. A response
     * of a layout that is no longer in the canvas is ignored.
     */
    const updatePanels = (layoutId, update) => {
        setParamsContext((prev) => (prev?.layout?.id === layoutId
            ? { ...prev, layout: { ...prev.layout, panels: update(prev.layout.panels) } }
            : prev));
    };

    /**
     * Stores the position and size of the panels of a layout.
     *
     * @param {Object} layout - Layout with the panels to store.
     * @param {boolean} notify - Shows the updated toast; a deleted panel already shows its own.
     */
    const saveGeometry = (layout, notify = false) => {
        if (!layout?.panels?.length) return;
        dispatch(UpdateEventMonitoringPanelsGeometry(LAYOUT_MODEL.toGeometry(layout))).then(({ payload: { data } }) => {
            if (!data?.success) return errorMsg(data?.code);
            if (notify) toast.success(t('has_been_updated_successfully'));
        });
    };

    // An admin that cannot create layouts only sees the saved ones //
    const openLayoutsModal = (tab = LAYOUTS_MODAL_TAB.CREATE.ID) => {
        setLayoutsTab(canCreate ? tab : LAYOUTS_MODAL_TAB.SAVED.ID);
        setShowLayoutsModal(true);
    };

    const handleCreateLayout = (form) => {
        dispatch(CreateEventMonitoringLayout(CREATE_LAYOUT_MODEL.toCreate(form))).then(({ payload: { data } }) => {
            if (!data?.success) return errorMsg(data?.code);

            showLayout(data.data);
            setShowLayoutsModal(false);
            toast.success(t('has_been_created_successfully'));
        });
    };

    const handleOpenLayout = (layoutId) => {
        setShowLayoutsModal(false);
        if (layoutId === activeLayout?.id) return;
        dispatch(SetActiveEventMonitoringLayout(layoutId)).then(({ payload: { data } }) => {
            if (!data?.success) return errorMsg(data?.code);
            showLayout(data.data);
        });
    };

    const handleRenameLayout = (layoutId, name) => {
        dispatch(UpdateEventMonitoringLayout({ id: layoutId, name: name.trim() })).then(({ payload: { data } }) => {
            if (!data?.success) return errorMsg(data?.code);

            setParamsContext((prev) => (prev?.layout?.id === layoutId
                ? { ...prev, layout: { ...prev.layout, name: data.data?.name } }
                : prev));
            setSavedLayoutsModified(true);
            toast.success(t('has_been_updated_successfully'));
        });
    };

    /**
     * Adds a panel at the end of the active layout, in the first free cell after the last panel.
     * The new panel takes one cell of the structure of the layout type.
     */
    const handleAddPanel = (form) => {
        if (!activeLayout) return;

        const layoutId = activeLayout.id;
        const geometry = getPanelsGeometry([...activeLayout.panels, getCellUnits(getLayoutType(activeLayout.typeId))], activeLayout.columns)[activeLayout.panels.length];
        dispatch(CreateEventMonitoringPanel(PANEL_MODEL.toCreate(layoutId, form, geometry))).then(({ payload: { data } }) => {
            if (!data?.success) return errorMsg(data?.code);

            updatePanels(layoutId, (panels) => [...panels, PANEL_MODEL.fromApi(data.data)]);
            setShowAddPanelModal(false);
            toast.success(t('has_been_added_successfully'));
        });
    };

    /**
     * Changes the source of a panel. Its size is kept from the canvas, it can be changing.
     */
    const handleConfigurePanel = (panelId, form) => {
        const layoutId = activeLayout?.id;
        // The first configuration of a panel creates its data source, the next ones update it //
        const wasConfigured = isConfiguredPanel(activeLayout?.panels?.find((panel) => panel.id === panelId));
        dispatch(UpdateEventMonitoringPanel(PANEL_MODEL.toUpdate(panelId, form))).then(({ payload: { data } }) => {
            if (!data?.success) return errorMsg(data?.code);

            const { colSpan, rowSpan, ...source } = PANEL_MODEL.fromApi(data.data);
            updatePanels(layoutId, (panels) => panels.map((panel) => (panel.id === panelId ? { ...panel, ...source } : panel)));
            setPanelToConfigure(null);
            setPanelToExpand((prev) => (prev?.id === panelId ? { ...prev, ...source } : prev));
            toast.success(t(wasConfigured ? 'has_been_updated_successfully' : 'has_been_created_successfully'));
        });
    };

    /**
     * Applies the spans of a resized panel, limited to the units of the canvas. The canvas reflows by
     * itself, so neighbors only move.
     */
    const handleResizePanel = (panelId, colSpan, rowSpan) => {
        const layout = paramsRef.current?.layout;
        if (!layout) return;

        const shape = getPanelShape(colSpan, rowSpan, layout.columns, GRID_UNITS);
        updatePanels(layout.id, (panels) => panels.map((panel) => (panel.id === panelId
            ? { ...panel, colSpan: shape.colSpan, rowSpan: shape.rowSpan }
            : panel)));
    };

    /**
     * Stores the canvas once a resize ends, the panels after the resized one may have moved.
     */
    const handleResizePanelEnd = () => {
        saveGeometry(paramsRef.current?.layout, true);
    };

    const handleConfirmDelete = () => {
        const target = deleteTarget;

        if (target?.type === DELETE_TARGET.LAYOUT.ID) {
            dispatch(DeleteEventMonitoringLayout(target.id)).then(({ payload: { data } }) => {
                if (!data?.success) return errorMsg(data?.code);

                // The backend loads the oldest remaining layout when the one of the canvas is deleted //
                if (target.id === paramsRef.current?.layout?.id) loadActiveLayout();
                setSavedLayoutsModified(true);
                toast.success(t('has_been_deleted_successfully'));
            });
        } else if (target?.type === DELETE_TARGET.PANEL.ID) {
            const layout = activeLayout;
            dispatch(DeleteEventMonitoringPanel(target.id)).then(({ payload: { data } }) => {
                if (!data?.success) return errorMsg(data?.code);

                updatePanels(layout?.id, (panels) => panels.filter((panel) => panel.id !== target.id));
                setPanelFeeds((prev) => Object.fromEntries(Object.entries(prev).filter(([panelId]) => panelId !== target.id)));
                setPanelToExpand((prev) => (prev?.id === target.id ? null : prev));
                // The panels after the deleted one move to its cells //
                saveGeometry({ ...layout, panels: layout?.panels?.filter((panel) => panel.id !== target.id) });
                toast.success(t('has_been_deleted_successfully'));
            });
        }
    };

    /**
     * Opens the modal a step of the walkthrough explains and closes the one of the previous step.
     * Step 0 is the welcome card, steps 1..n are the walkthrough steps of the admin.
     */
    const applyWalkthroughModal = (step) => {
        const modal = step > 0 ? walkthroughSteps[step - 1]?.MODAL : null;
        if (modal === WALKTHROUGH_MODAL.LAYOUTS) {
            openLayoutsModal(LAYOUTS_MODAL_TAB.CREATE.ID);
        } else {
            setShowLayoutsModal(false);
        }
        // The add panel modal is shown even without a layout, as an example of the step: it closes with the step and
        // its confirm does nothing until a layout exists //
        setShowAddPanelModal(modal === WALKTHROUGH_MODAL.ADD_PANEL);
    };

    const closeWalkthrough = () => {
        applyWalkthroughModal(0);
        setWalkthroughStep(null);
        dispatch(SetEventMonitoringWalkthroughDone()).then(({ payload: { data } }) => {
            if (!data?.success) return errorMsg(data?.code);
            // The preferences are read again so the module does not show it when it is opened again //
            if (user?.id) dispatch(GetUserPreferencesByUserId(user.id));
        });
    };

    const nextWalkthroughStep = () => {
        if (walkthroughStep === null || walkthroughStep >= walkthroughSteps.length) {
            closeWalkthrough();
            return;
        }
        const step = walkthroughStep + 1;
        applyWalkthroughModal(step);
        setWalkthroughStep(step);
    };

    /**
     * The notifications bring the newest reports every time one changes, so the report panels receive them
     * as new events: a new report is added and the ones already shown take their new status.
     */
    useEffect(() => {
        if (reports?.length) pushPanelEvents(reports.map(toReportEvent));
    }, [reports]);

    /**
     * Events read for a panel.
     */
    const getPanelFeed = (panel) => {
        const feed = panelFeeds[panel?.id];
        return feed?.sourceKey === getPanelSourceKey(panel) ? feed : PANEL_FEED_MODEL;
    };

    // The steps of the walkthrough that point to a panel draw an example one while the canvas has none //
    const isWalkthroughMock = walkthroughStep > 0 && !!walkthroughSteps[walkthroughStep - 1]?.PANEL && !activeLayout?.panels?.length;
    const boardLayout = isWalkthroughMock ? getWalkthroughMockLayout(activeLayout, t) : activeLayout;

    return {
        t,
        canRead,
        canCreate,
        canUpdate,
        canDelete,
        isOnline,
        setModified,
        getPanelFeed,
        loadPanelEvents,
        loadAreaEvents,
        now,
        activeLayout,
        boardLayout,
        isWalkthroughMock,
        showLayoutsModal,
        setShowLayoutsModal,
        layoutsTab,
        setLayoutsTab,
        savedLayoutsModified,
        setSavedLayoutsModified,
        openLayoutsModal,
        showAddPanelModal,
        setShowAddPanelModal,
        panelToConfigure,
        setPanelToConfigure,
        panelToExpand,
        setPanelToExpand,
        deleteTarget,
        setDeleteTarget,
        deleteTargetModel,
        walkthroughStep,
        walkthroughSteps,
        handleCreateLayout,
        handleOpenLayout,
        handleRenameLayout,
        handleAddPanel,
        handleConfigurePanel,
        handleResizePanel,
        handleResizePanelEnd,
        handleConfirmDelete,
        closeWalkthrough,
        nextWalkthroughStep
    };
};

export default EventMonitoringMainApp;
